import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  IAccount,
  IClaimantInfo,
  ICompensation,
  IExpenseProcessed,
  IExpenseRequest,
  IOrganization,
} from "../interfaces";

import pack from "../../package.json";
import {
  login,
  logoutAPI,
  ping,
  resetPassword,
  sendRegNum,
  startRegistration,
  uploadFiles,
} from "./UserAPI";
import Cookies from "js-cookie";
import { RootState } from "./store";
import {
  docusignAction,
  downloadFormAction,
  generateFormAction,
  handleOnlineSignatureAcction,
  sendNotificationAcction,
  submitRequestAction,
} from "./FormsSlice";

export interface IUserSlice {
  err_msg: string | null;
  isLoading: boolean;
  msg: string | null;
  claimantInfo: IClaimantInfo | null;
  compensations: ICompensation[] | null;
  services: IOrganization[] | null;
  account: IAccount | null;
  servicesTitles: { title: string; subTitle: string } | null;
  be_version: undefined | string;
  fe_version: string;
  deployDate: string | undefined;
  underConstruction: boolean;
  expenses: IExpenseProcessed[] | [];
  localOffice: { address: string; email: string } | null;
}

export const UserState: IUserSlice = {
  err_msg: null,
  msg: null,
  isLoading: false,
  compensations: null,
  claimantInfo: null,
  services: null,
  account: null,
  servicesTitles: null,
  be_version: Cookies.get("be_v"),
  fe_version: pack.version,
  deployDate: Cookies.get("deploy"),
  underConstruction: Cookies.get("underConstruction")
    ? (JSON.parse(
        Cookies.get("underConstruction") as "false" | "true"
      ) as boolean)
    : false,
  expenses: [],
  localOffice: null,
};

export const uploadFilesAction = createAsyncThunk(
  "uploadFiles",
  async (args: {
    formData: FormData;
    fund: string;
    regNum: string;
    expense: IExpenseRequest;
  }) => {
    const response = await uploadFiles(args);
    return response;
  }
);

export const userSlice = createSlice({
  name: "userSlice",
  initialState: UserState,
  reducers: {
    setErrMsg: (state, action: PayloadAction<string | null>) => {
      state.err_msg = action.payload;
    },
    setMsg: (state, action: PayloadAction<string | null>) => {
      state.msg = action.payload;
    },
    resetUserState: (state) => {
      state.compensations = null;
      state.claimantInfo = null;
      state.services = null;
      state.account = null;
      state.servicesTitles = null;
      state.expenses = [];
      state.localOffice = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.claimantInfo = action.payload.claimantInfo;
        state.account = action.payload.account;
        state.expenses = action.payload.expenses;
      })
      .addCase(login.rejected, (state, action: any) => {
        state.err_msg = action.payload
          ? action.payload
          : "Unknown server error";
        state.isLoading = false;
      })
      .addCase(
        startRegistration.pending,
        (state, action: PayloadAction<any | null>) => {
          state.isLoading = true;
        }
      )
      .addCase(
        startRegistration.fulfilled,
        (state, action: PayloadAction<any | null>) => {
          const { result } = action.payload;
          if (!result) {
            state.err_msg = action.payload.msg;
            state.isLoading = false;
            return;
          }
          state.isLoading = false;
          state.err_msg = null;
          state.msg = action.payload.msg;
        }
      )
      .addCase(startRegistration.rejected, (state, action) => {
        state.isLoading = false;
        state.err_msg = action.error.message
          ? action.error.message
          : "Unknown server error";
      })
      .addCase(
        downloadFormAction.pending,
        (state, action: PayloadAction<any | null>) => {
          state.isLoading = true;
        }
      )
      .addCase(
        downloadFormAction.fulfilled,
        (state, action: PayloadAction<any | null>) => {
          state.isLoading = false;
          state.err_msg = null;
          state.msg = "Download Completed";
        }
      )
      .addCase(downloadFormAction.rejected, (state, action) => {
        state.isLoading = false;
        let errMsg = action.error.message
          ? action.error.message
          : "Unknown server error";
        if (action.error.message === "Request failed with status code 401") {
          errMsg = "The session has expired. Login is required";
          state.err_msg = errMsg;
          state.compensations = null;
          state.claimantInfo = null;
          state.services = null;
          state.account = null;
          state.servicesTitles = null;
          state.expenses = [];
          state.localOffice = null;
          return;
        }
        state.err_msg = errMsg;
      })
      .addCase(ping.pending, (state, action) => {
        state.isLoading = true;
      })

      .addCase(ping.fulfilled, (state, action) => {
        const { result } = action.payload.data;

        if (!result) {
          state.isLoading = false;
          return;
        }
        const claimantInfo = action.payload.data.claimantInfo!;

        state.isLoading = false;
        state.err_msg = null;
        state.account = action.payload.data.account;
        state.claimantInfo = claimantInfo;
        state.expenses = action.payload.data.expenses;
        state.isLoading = false;
      })

      .addCase(ping.rejected, (state, action: any) => {
        state.isLoading = false;
        console.log(action);
        if (state.account) {
          if (!action.payload) {
            state.err_msg = "Cannot connect to server";
            return;
          }
          if (
            action.payoad &&
            action.payload.msg &&
            action.payload.msg !== "Missing token"
          ) {
            state.err_msg = "Session timeout. Please login again.";
          } else {
            state.err_msg = action.payload.msg
              ? action.payload.msg
              : "Unknown server error";
          }
        }
      })
      .addCase(sendRegNum.pending, (state, action) => {
        state.isLoading = true;
      })

      .addCase(sendRegNum.fulfilled, (state, action) => {
        const { result } = action.payload;
        if (!result) {
          state.err_msg = action.payload.msg;
          state.isLoading = false;
          return;
        }
        state.isLoading = false;
        state.msg = action.payload.msg;
        state.err_msg = null;
        state.isLoading = false;
      })

      .addCase(sendRegNum.rejected, (state, action) => {
        state.isLoading = false;
        state.err_msg = action.error.message
          ? action.error.message
          : "Unknown server error";
      })
      .addCase(resetPassword.pending, (state, action) => {
        state.isLoading = true;
      })

      .addCase(resetPassword.fulfilled, (state, action) => {
        const { result } = action.payload;
        if (!result) {
          state.err_msg = action.payload.msg;
          state.isLoading = false;
          return;
        }
        state.isLoading = false;
        state.err_msg = null;
        state.msg = action.payload.msg;
        state.isLoading = false;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.err_msg = action.error.message
          ? action.error.message
          : "Unknown server error";
      })
      .addCase(uploadFilesAction.pending, (state, action) => {
        state.isLoading = true;
      })

      .addCase(uploadFilesAction.fulfilled, (state, action) => {
        const { result } = action.payload;
        if (!result) {
          state.msg = action.payload.msg;
          state.isLoading = false;
          return;
        }
        state.isLoading = false;
        state.msg =
          action.payload.msg === "success"
            ? "Upload Successful"
            : action.payload.msg;
        state.err_msg = null;
        state.isLoading = false;
      })

      .addCase(uploadFilesAction.rejected, (state, action) => {
        state.isLoading = false;
        console.log(action.payload);
        console.log(action.error);
        state.err_msg =
          action.error.message === "Request failed with status code 401"
            ? "Session timeout. Please login again."
            : action.error.message
            ? action.error.message
            : "Unknown server error.";
      })
      .addCase(sendNotificationAcction.pending, (state, action) => {
        state.isLoading = true;
      })

      .addCase(sendNotificationAcction.fulfilled, (state, action) => {
        const { result } = action.payload;
        if (!result) {
          state.msg = action.payload.msg;
          state.isLoading = false;
          return;
        }
        state.isLoading = false;
        state.err_msg = null;
        state.isLoading = false;
      })

      .addCase(sendNotificationAcction.rejected, (state, action) => {
        state.isLoading = false;
        state.err_msg = action.error.message
          ? action.error.message
          : "Unknown server error";
      })

      .addCase(logoutAPI.pending, (state, action) => {
        state.isLoading = true;
      })

      .addCase(logoutAPI.fulfilled, (state, action) => {
        const { result, msg } = action.payload;
        state.claimantInfo = null;
        state.compensations = null;
        state.services = null;
        state.account = null;
        state.err_msg = msg ? "Session timeout. Please log back in" : "";
        state.isLoading = false;
      })
      .addCase(logoutAPI.rejected, (state, action) => {
        state.isLoading = false;
        state.err_msg = "Unknown server error";
        state.claimantInfo = null;
        state.compensations = null;
        state.services = null;
        state.account = null;
        state.err_msg = null;
        state.isLoading = false;
      })
      .addCase(generateFormAction.pending, (state, action) => {
        state.isLoading = true;
      })

      .addCase(generateFormAction.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(generateFormAction.rejected, (state, action) => {
        state.isLoading = false;
        let errMsg = action.error.message
          ? action.error.message
          : "Unknown server error";
        if (action.error.message === "Request failed with status code 401") {
          errMsg = "The session has expired. Login is required";
          state.err_msg = errMsg;
          state.compensations = null;
          state.claimantInfo = null;
          state.services = null;
          state.account = null;
          state.servicesTitles = null;
          state.expenses = [];
          state.localOffice = null;
          return;
        }
        state.err_msg = errMsg;
      })

      .addCase(submitRequestAction.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(submitRequestAction.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(submitRequestAction.rejected, (state, action) => {
        state.isLoading = false;
        let errMsg = action.error.message
          ? action.error.message
          : "unknown server error";
        if (action.error.message === "Request failed with status code 401") {
          errMsg = "The session has expired. Login is required";
          state.err_msg = errMsg;
          state.compensations = null;
          state.claimantInfo = null;
          state.services = null;
          state.account = null;
          state.servicesTitles = null;
          state.expenses = [];
          state.localOffice = null;
          return;
        }
        state.err_msg = errMsg;
      })
      .addCase(handleOnlineSignatureAcction.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(handleOnlineSignatureAcction.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(handleOnlineSignatureAcction.rejected, (state, action) => {
        state.isLoading = false;
        let errMsg = action.error.message
          ? action.error.message
          : "unknown server error";
        if (action.error.message === "Request failed with status code 401") {
          errMsg = "The session has expired. Login is required";
          state.err_msg = errMsg;
          state.compensations = null;
          state.claimantInfo = null;
          state.services = null;
          state.account = null;
          state.servicesTitles = null;
          state.expenses = [];
          state.localOffice = null;
          return;
        }
        state.err_msg = errMsg;
      });
  },
});

export const { setErrMsg, setMsg, resetUserState } = userSlice.actions;

export const selectErrMsg = (state: RootState) => state.userSlice.err_msg;
export const selectMsg = (state: RootState) => state.userSlice.msg;
export const selectClaimInfo = (state: RootState) =>
  state.userSlice.claimantInfo;
export const selectRegNum = (state: RootState) =>
  state.userSlice.claimantInfo?.REG_NUMBER;
export const selectCompensations = (state: RootState) =>
  state.userSlice.compensations;
export const selectServices = (state: RootState) => state.userSlice.services;
export const selectAccount = (state: RootState) => state.userSlice.account;
export const isLoading = (state: RootState) => state.userSlice.isLoading;
export const selectTitle = (state: RootState) =>
  state.userSlice.servicesTitles?.title;
export const selectSubTitle = (state: RootState) =>
  state.userSlice.servicesTitles?.subTitle;
export const selectBEversion = (state: RootState) => state.userSlice.be_version;
export const selectFEversion = (state: RootState) => state.userSlice.fe_version;
export const selectDeployDate = (state: RootState) =>
  state.userSlice.deployDate;
export const selectUnderConstruction = (state: RootState) =>
  state.userSlice.underConstruction;
export const selectExpenses = (state: RootState) => state.userSlice.expenses;
export const selectLocalOfficeInfo = (state: RootState) =>
  state.userSlice.localOffice;

export default userSlice.reducer;
